import React, { useState, useEffect, useRef } from 'react';
import {
  Alert,
  Box,
  TextField,
  Typography,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  FormHelperText,
  Switch,
  FormControlLabel,
  Button,
  Paper,
  Divider,
  IconButton,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Code as CodeIcon,
} from '@mui/icons-material';
import { MODULE_TYPES } from '../../utils/constants';
import DatabaseConfig from './DatabaseConfig';
import MongoModuleConfig, { MONGO_MODULES } from './MongoModuleConfig';
import AIModelSelect, { DEFAULT_MODELS, MODEL_FIELD_NAMES } from '../common/AIModelSelect';

// Fields that are now handled globally in Settings > AI Configuration
const GLOBAL_AI_FIELDS = new Set([
  'api_key',
  'openai_api_key',
  'anthropic_api_key',
  'google_api_key',
  'api_base_url',
  'ollama_url',
  'local_api_url',
]);

const ConfigField = ({ field, value, onChange, error }) => {
  const [fieldValue, setFieldValue] = useState(value || '');
  
  useEffect(() => {
    setFieldValue(value || '');
  }, [value]);
  
  const handleChange = (newValue) => {
    setFieldValue(newValue);
    onChange(field.name, newValue);
  };
  
  switch (field.type) {
    case 'string':
      return (
        <TextField
          label={field.label || field.name}
          value={fieldValue}
          onChange={(e) => handleChange(e.target.value)}
          fullWidth
          margin="dense"
          size="small"
          error={Boolean(error)}
          helperText={error || field.description}
          required={field.required}
        />
      );
      
    case 'number':
      return (
        <TextField
          label={field.label || field.name}
          value={fieldValue}
          onChange={(e) => handleChange(Number(e.target.value))}
          type="number"
          fullWidth
          margin="dense"
          size="small"
          error={Boolean(error)}
          helperText={error || field.description}
          required={field.required}
        />
      );
      
    case 'boolean':
      return (
        <FormControlLabel
          control={
            <Switch
              checked={Boolean(fieldValue)}
              onChange={(e) => handleChange(e.target.checked)}
              color="primary"
            />
          }
          label={field.label || field.name}
        />
      );
      
    case 'select':
      return (
        <FormControl 
          fullWidth 
          margin="dense" 
          size="small"
          error={Boolean(error)}
          required={field.required}
        >
          <InputLabel>{field.label || field.name}</InputLabel>
          <Select
            value={fieldValue}
            onChange={(e) => handleChange(e.target.value)}
            label={field.label || field.name}
          >
            {field.options.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
          {(error || field.description) && (
            <FormHelperText>{error || field.description}</FormHelperText>
          )}
        </FormControl>
      );
      
    case 'multiline':
      return (
        <TextField
          label={field.label || field.name}
          value={fieldValue}
          onChange={(e) => handleChange(e.target.value)}
          fullWidth
          margin="dense"
          size="small"
          multiline
          rows={4}
          error={Boolean(error)}
          helperText={error || field.description}
          required={field.required}
        />
      );
      
    case 'json':
      return (
        <TextField
          label={field.label || field.name}
          value={typeof fieldValue === 'object' ? JSON.stringify(fieldValue, null, 2) : fieldValue}
          onChange={(e) => {
            try {
              const parsed = JSON.parse(e.target.value);
              handleChange(parsed);
            } catch (error) {
              // Allow invalid JSON during editing, but store as string
              handleChange(e.target.value);
            }
          }}
          fullWidth
          margin="dense"
          size="small"
          multiline
          rows={4}
          error={Boolean(error)}
          helperText={error || field.description}
          required={field.required}
          InputProps={{
            sx: { fontFamily: 'monospace' }
          }}
        />
      );
      
    case 'array':
      const items = Array.isArray(fieldValue) ? fieldValue : [];
      
      return (
        <Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography variant="body2">{field.label || field.name}</Typography>
            <Button
              startIcon={<AddIcon />}
              size="small"
              onClick={() => handleChange([...items, ''])}
            >
              Add
            </Button>
          </Box>
          
          {items.map((item, index) => (
            <Box key={index} sx={{ display: 'flex', mb: 1 }}>
              <TextField
                value={item}
                onChange={(e) => {
                  const newItems = [...items];
                  newItems[index] = e.target.value;
                  handleChange(newItems);
                }}
                fullWidth
                size="small"
                margin="dense"
              />
              <IconButton
                size="small"
                color="error"
                onClick={() => {
                  const newItems = items.filter((_, i) => i !== index);
                  handleChange(newItems);
                }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Box>
          ))}
          
          {(error || field.description) && (
            <FormHelperText error={Boolean(error)}>
              {error || field.description}
            </FormHelperText>
          )}
        </Box>
      );
      
    default:
      return (
        <TextField
          label={field.label || field.name}
          value={fieldValue}
          onChange={(e) => handleChange(e.target.value)}
          fullWidth
          margin="dense"
          size="small"
          error={Boolean(error)}
          helperText={error || field.description}
          required={field.required}
        />
      );
  }
};

const NodeConfigPanel = ({
  nodeId,
  nodeType,
  moduleType,
  moduleName,
  config = {},
  onConfigChange,
  modules = [],
  selectedNode,
}) => {
  const [errors, setErrors] = useState({});

  // Ref to track the latest provider synchronously, so handleAIModelChange
  // always uses the correct provider even when called right after a provider
  // change (before React re-renders and updates config).
  const providerRef = useRef(config.provider || 'openai');
  providerRef.current = config.provider || 'openai';

  // Find module definition -- modules is a flat array
  const moduleList = Array.isArray(modules) ? modules : (modules.modules || []);
  const moduleDefinition = moduleList.find(
    m => m.type === moduleType && m.module_name === moduleName
  );

  // Get available fields for this module
  const getConfigFields = () => {
    if (!moduleDefinition || !moduleDefinition.config_schema) {
      return [];
    }
    return moduleDefinition.config_schema;
  };

  const configFields = getConfigFields();

  // Detect whether this is an AI module (has a 'provider' field in schema)
  const isAIModule = configFields.some((f) => f.name === 'provider');

  // Fields managed by the unified AI provider/model selector -- exclude from the generic form
  const AI_MANAGED_FIELDS = new Set([
    'provider',
    'model',
    'ollama_model',
    'google_model',
  ]);

  // The fields we actually render in the generic config form
  const visibleConfigFields = configFields.filter((field) => {
    // Always hide globally-managed AI key/URL fields
    if (GLOBAL_AI_FIELDS.has(field.name)) return false;
    // For AI modules, also hide the provider/model fields (handled by AIModelSelect)
    if (isAIModule && AI_MANAGED_FIELDS.has(field.name)) return false;
    return true;
  });

  // Handle field change
  const handleFieldChange = (fieldName, value) => {
    const newConfig = { ...config, [fieldName]: value };

    // Clear error when field is updated
    if (errors[fieldName]) {
      const newErrors = { ...errors };
      delete newErrors[fieldName];
      setErrors(newErrors);
    }

    onConfigChange(nodeId, newConfig);
  };

  // Handle AI provider change
  const handleAIProviderChange = (newProvider) => {
    // Update the ref synchronously so that the subsequent handleAIModelChange
    // call (triggered by AIModelSelect right after this) reads the new provider.
    providerRef.current = newProvider;

    const newConfig = { ...config, provider: newProvider };
    // Clear old model fields
    delete newConfig.model;
    delete newConfig.ollama_model;
    delete newConfig.google_model;
    onConfigChange(nodeId, newConfig);
  };

  // Handle AI model change
  const handleAIModelChange = (newModel) => {
    // Read provider from the ref rather than from config, because this callback
    // can be invoked immediately after handleAIProviderChange (before React
    // re-renders), in which case config still holds the old provider value.
    const provider = providerRef.current;
    const modelField = MODEL_FIELD_NAMES[provider] || 'model';

    // Build new config from the current config, ensuring the provider is set
    // correctly (it may not yet be reflected in config due to batched updates).
    const newConfig = { ...config, provider, [modelField]: newModel };
    // Remove stale model fields from other providers
    Object.values(MODEL_FIELD_NAMES).forEach((field) => {
      if (field !== modelField) {
        delete newConfig[field];
      }
    });
    onConfigChange(nodeId, newConfig);
  };

  // Get the current model value based on the current provider
  const getCurrentModel = () => {
    const provider = providerRef.current;
    const modelField = MODEL_FIELD_NAMES[provider] || 'model';
    return config[modelField] || DEFAULT_MODELS[provider] || '';
  };

  const isMongoModule = MONGO_MODULES.includes(moduleName);
  const isDatabaseModule = moduleName === 'database_query' || moduleName === 'database_write';

  if (!moduleDefinition) {
    return (
      <Box sx={{ p: 2 }}>
        <Paper sx={{ p: 2, bgcolor: 'background.default' }}>
          <Typography variant="body2" color="text.secondary">
            No configuration available for this module.
          </Typography>
        </Paper>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="subtitle2" gutterBottom>
        Configuration
      </Typography>

      {selectedNode?.data?.customModuleId && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Defaults from custom module preset. You can override any field.
        </Alert>
      )}

      {/* AI Provider / Model selector for AI modules */}
      {isAIModule && (
        <Box sx={{ mb: 2 }}>
          <AIModelSelect
            provider={config.provider || 'openai'}
            model={getCurrentModel()}
            onProviderChange={handleAIProviderChange}
            onModelChange={handleAIModelChange}
            size="small"
            direction="column"
            showHelperText
          />
        </Box>
      )}

      {isMongoModule ? (
        <MongoModuleConfig nodeId={nodeId} moduleName={moduleName} config={config} onConfigChange={onConfigChange} />
      ) : visibleConfigFields.length > 0 ? (
        <Box>
          {visibleConfigFields.map((field) => (
            <Box key={field.name} sx={{ mb: 2 }}>
              <ConfigField
                field={field}
                value={config[field.name]}
                onChange={handleFieldChange}
                error={errors[field.name]}
              />
            </Box>
          ))}
        </Box>
      ) : !isAIModule ? (
        <Paper sx={{ p: 2, bgcolor: 'background.default' }}>
          <Typography variant="body2" color="text.secondary">
            This module does not have any configurable properties.
          </Typography>
        </Paper>
      ) : null}
      
      {/* Database schema browser for database modules */}
      {isDatabaseModule && (
        <Box sx={{ mt: 3 }}>
          <Divider sx={{ mb: 2 }} />
          <DatabaseConfig
            nodeId={nodeId}
            moduleName={moduleName}
            config={config}
            onConfigChange={onConfigChange}
          />
        </Box>
      )}

      {/* For custom code modules, show code editor */}
      {moduleType === MODULE_TYPES.CUSTOM_CODE && (
        <Box sx={{ mt: 3 }}>
          <Divider sx={{ mb: 2 }} />
          <Typography variant="subtitle2" gutterBottom>
            Custom Code
          </Typography>
          <TextField
            value={config.code || ''}
            onChange={(e) => handleFieldChange('code', e.target.value)}
            fullWidth
            multiline
            rows={10}
            InputProps={{
              sx: { fontFamily: 'monospace' },
              startAdornment: (
                <Box sx={{ position: 'absolute', right: 8, top: 8 }}>
                  <IconButton size="small">
                    <CodeIcon fontSize="small" />
                  </IconButton>
                </Box>
              ),
            }}
          />
        </Box>
      )}
    </Box>
  );
};

export default NodeConfigPanel;
